// ---------------------------------------------------------------------------
// FEMA IPAWS status/advisory code registry — every status item FEMA can return
// on a CAP submission, grouped by the response channel that issued it.
// SOURCE: FEMA IPAWS-OPEN "Status Item Response Detail" table (RescAlert local
// copy, 2026-09-02). `isError` is FEMA's own Error column (Y/N). `disseminated`
// is RescAlert's addition: `true` marks the channel's own success Ack, `false`
// marks the channel's own "not disseminated" status — every other code is a
// validation/authorization detail with no direct bearing on dissemination.
// ---------------------------------------------------------------------------
import type { FemaStatusChannel, FemaStatusCodeInfo } from 'types/fema.types';

const MOBILE_CARRIER: FemaStatusChannel = 'MOBILE_CARRIER';
const CAP_EXCHANGE: FemaStatusChannel = 'CAP_EXCHANGE';
const IPAWS_PROFILE: FemaStatusChannel = 'IPAWS_PROFILE';
const NWEM: FemaStatusChannel = 'NWEM';
const EAS: FemaStatusChannel = 'EAS';
const CMAS: FemaStatusChannel = 'CMAS';
const PUBLIC_FEED: FemaStatusChannel = 'PUBLIC_FEED';

/** Keyed by FEMA status item code, as a string (the wire form). */
export const FEMA_STATUS_CODES: Record<string, FemaStatusCodeInfo> = {
  // Mobile Carrier Gateway Responses
  '10': {
    code: '10',
    channel: MOBILE_CARRIER,
    isError: false,
    label: 'Ack',
    remark:
      'CAP alert was successfully transformed into a CMAS CMAC message and disseminated to the mobile carrier identified in the message status response.',
    disseminated: true,
  },
  '100': {
    code: '100',
    channel: MOBILE_CARRIER,
    isError: true,
    label: 'invalid-federal-alert-gateway-id',
    remark:
      'Possible error returned when CMAS-compliant CAP alerts are transformed and disseminated to mobile carrier gateways.',
  },
  '101': {
    code: '101',
    channel: MOBILE_CARRIER,
    isError: true,
    label: 'protocol-version-not-supported',
    remark: 'The requested protocol version is not supported.',
  },
  '102': {
    code: '102',
    channel: MOBILE_CARRIER,
    isError: true,
    label: 'Server-error',
    remark: 'A server error occurred.',
  },
  '103': {
    code: '103',
    channel: MOBILE_CARRIER,
    isError: true,
    label: 'Invalid-format',
    remark: 'The message contains an invalid format.',
  },
  '104': {
    code: '104',
    channel: MOBILE_CARRIER,
    isError: true,
    label: 'Invalid-element',
    remark:
      'Possible causes include a non-GSM-7 character, invalid carriage return, invalid message expiration, or a gateway that does not support deduplication. A lack of deduplication support is not considered a message error.',
  },
  '105': {
    code: '105',
    channel: MOBILE_CARRIER,
    isError: true,
    label: 'missing-element',
    remark: 'A required element is missing.',
  },
  '106': {
    code: '106',
    channel: MOBILE_CARRIER,
    isError: true,
    label: 'operation-not-allowed',
    remark: 'The requested operation is not allowed.',
  },
  '107': {
    code: '107',
    channel: MOBILE_CARRIER,
    isError: true,
    label: 'operation-pre-empted',
    remark: 'The requested operation was preempted.',
  },
  '108': {
    code: '108',
    channel: MOBILE_CARRIER,
    isError: true,
    label: 'RMT-distribution-precluded',
    remark: 'RMT distribution was precluded.',
  },
  '198': {
    code: '198',
    channel: MOBILE_CARRIER,
    isError: true,
    label: 'gateway-endpoint-invalid-response',
    remark: 'The wireless carrier returned invalid XML or an empty response.',
  },
  '199': {
    code: '199',
    channel: MOBILE_CARRIER,
    isError: true,
    label: 'Gateway-endpoint-is-unreachable',
    remark: 'The CMAS gateway could not be reached or the request timed out.',
  },

  // CAP Exchange Responses
  '200': {
    code: '200',
    channel: CAP_EXCHANGE,
    isError: false,
    label: 'Ack',
    remark: 'Message validates properly against the CAP v1.2 schema.',
    validationSuccess: true,
  },
  '202': {
    code: '202',
    channel: CAP_EXCHANGE,
    isError: false,
    label: 'alert-signature-is-valid',
    remark: 'CAP Exchange validation indicates that the digital signature is valid.',
    validationSuccess: true,
  },
  '207': {
    code: '207',
    channel: CAP_EXCHANGE,
    isError: true,
    label: 'reference-element-missing',
    remark:
      'A CAP Alert Update, Cancel, Ack, or Error message is missing the <references> element.',
  },
  '208': {
    code: '208',
    channel: CAP_EXCHANGE,
    isError: true,
    label: 'Alert-signature-is-not-valid',
    remark: 'The digital signature or an aspect of the CAP schema is invalid.',
  },
  '209': {
    code: '209',
    channel: CAP_EXCHANGE,
    isError: true,
    label: 'message-references-expired-alert',
    remark: 'An Update, Cancel, Ack, or Error message references an expired CAP alert.',
  },
  '210': {
    code: '210',
    channel: CAP_EXCHANGE,
    isError: false,
    label: 'signer-not-authorized-for-broadcast-cog',
    remark:
      'Advisory indicating that the COG is not authorized to post to the Broadcast COG, although the CAP alert\u2019s <addresses> element includes it. The Broadcast COG will not be stored in metadata or made retrievable by all Alert Originators.',
  },
  '211': {
    code: '211',
    channel: CAP_EXCHANGE,
    isError: true,
    label: 'restricted-alert-restriction-element-missing-or-empty',
    remark: 'The <restriction> element is missing or empty when <scope> equals Restricted.',
  },
  '212': {
    code: '212',
    channel: CAP_EXCHANGE,
    isError: true,
    label: 'private-alert-addresses-element-missing-or-empty',
    remark: 'The <addresses> element is missing or empty when <scope> equals Private.',
  },
  '213': {
    code: '213',
    channel: CAP_EXCHANGE,
    isError: true,
    label: 'invalid-value-in-element-circle',
    remark: 'The <circle> value is not formatted as a paired center point and radius.',
  },
  '214': {
    code: '214',
    channel: CAP_EXCHANGE,
    isError: true,
    label: 'geographic-area-outside-authorized-boundary',
    remark:
      'The <area> block\u2019s <circle> or <polygon> falls outside the authorized FIPS or Marine Zone boundaries.',
  },
  '215': {
    code: '215',
    channel: CAP_EXCHANGE,
    isError: true,
    label: 'signer-not-authorized-for-event-code-CAPEXCH',
    remark:
      'When <eventCode><valueName> equals SAME, the Alert Originator\u2019s COG is not authorized for the specified event code.',
  },
  '216': {
    code: '216',
    channel: CAP_EXCHANGE,
    isError: true,
    label: 'invalid-value-in-element-polygon',
    remark:
      'The polygon contains an issue that may affect downstream systems, such as a zero-area collocated line segment, boundary crossover, duplicate vertices, or leading/trailing whitespace.',
  },
  '217': {
    code: '217',
    channel: CAP_EXCHANGE,
    isError: true,
    label: 'invalid-format-dates',
    remark: 'One or more dates use an invalid format.',
  },
  '218': {
    code: '218',
    channel: CAP_EXCHANGE,
    isError: true,
    label: 'referenced-alert-not-found',
    remark: 'The referenced CAP Alert Ack or Error message could not be found in IPAWS-OPEN.',
  },
  '219': {
    code: '219',
    channel: CAP_EXCHANGE,
    isError: true,
    label: 'signer-not-authorized-for-geocode-CAPEXCH',
    remark:
      'When <geocode><valueName> equals SAME, the Alert Originator\u2019s COG is not authorized for the specified geocode.',
  },
  '220': {
    code: '220',
    channel: CAP_EXCHANGE,
    isError: true,
    label: 'referenced-alert-previously-canceled',
    remark: 'The referenced alert has already been canceled.',
  },
  '221': {
    code: '221',
    channel: CAP_EXCHANGE,
    isError: true,
    label: 'invalid-CAPEXCHANGE-message',
    remark:
      'The CAP Exchange message is invalid. It will not be disseminated to EAS, WEA, or NWEM/NWR, but it remains retrievable through a GetRequest.',
  },
  '222': {
    code: '222',
    channel: CAP_EXCHANGE,
    isError: true,
    label: 'Restricted-characters-in-message',
    remark:
      '<identifier> contains a space, comma, double quotation mark, or restricted character: <, >, &, {, }, |, \\, ^, ~, [, or ]. <sender> contains a space, comma, double quotation mark, or restricted character: <, >, &, {, }, |, \\, ^, or ~.',
  },
  '223': {
    code: '223',
    channel: CAP_EXCHANGE,
    isError: true,
    label: 'invalid-value-in-element-geocode',
    remark:
      'Applies when <geocode><valueName> equals SAME. The <geocode><value> is not six digits or contains an invalid value.',
  },
  '224': {
    code: '224',
    channel: CAP_EXCHANGE,
    isError: true,
    label: 'reference-element-invalid',
    remark:
      'The <references> element is not structured as sender,identifier,sent. If the format is correct but the alert cannot be found, IPAWS returns status 218.',
  },
  '225': {
    code: '225',
    channel: CAP_EXCHANGE,
    isError: false,
    label: 'source-element-is-missing-or-empty',
    remark: 'Advisory indicating that the <source> parameter is missing or empty.',
  },

  // IPAWS Profile Validation Responses
  '300': {
    code: '300',
    channel: IPAWS_PROFILE,
    isError: false,
    label: 'Ack',
    remark: 'Message validates properly against the IPAWS Profile schema.',
  },
  '301': {
    code: '301',
    channel: IPAWS_PROFILE,
    isError: false,
    label: 'non-IPAWS-alert',
    remark:
      'The alert is considered non-IPAWS when <status> is not Actual, <code> is not IPAWSv1.0, or an info block is missing from a message that is not a Cancel.',
    disseminated: false,
  },
  '302': {
    code: '302',
    channel: IPAWS_PROFILE,
    isError: true,
    label: 'invalid-value-in-element-eventcode-and-category',
    remark:
      '<category> values differ among info blocks. Category values must be identical across all info blocks.',
  },
  '303': {
    code: '303',
    channel: IPAWS_PROFILE,
    isError: true,
    label: 'invalid-value-in-element-sent',
    remark: 'The <sent> time is not within five minutes of the current time.',
  },
  '304': {
    code: '304',
    channel: IPAWS_PROFILE,
    isError: true,
    label: 'invalid-value-in-element-expires',
    remark: 'The expiration time occurs before the sent time.',
  },
  '305': {
    code: '305',
    channel: IPAWS_PROFILE,
    isError: true,
    label: 'invalid-value-in-element-geocode',
    remark:
      'The <area> block is missing, <geocode><value> is not six digits, or <geocode><valueName> is not SAME. Every info block must pass these validations.',
  },
  '306': {
    code: '306',
    channel: IPAWS_PROFILE,
    isError: true,
    label: 'missing-required-element-expires',
    remark: 'The <expires> element is missing or incorrectly formatted.',
  },
  '311': {
    code: '311',
    channel: IPAWS_PROFILE,
    isError: true,
    label: 'invalid-value-in-element-eventcode-or-missing-eventcode',
    remark:
      'The SAME event code is missing or invalid for IPAWS dissemination. This may also occur when multiple SAME event codes are included.',
  },
  '312': {
    code: '312',
    channel: IPAWS_PROFILE,
    isError: true,
    label: 'signer-not-authorized-for-value-EAS-ORG',
    remark:
      'The SAME Organization code does not match the Organization code authorized in the Alert Originator\u2019s COG Profile.',
  },
  '313': {
    code: '313',
    channel: IPAWS_PROFILE,
    isError: true,
    label: 'web-token-invalid',
    remark:
      'The <web> value is missing http:// or https://, is missing a period, contains a space or ?, or exceeds 2,083 characters.',
  },
  '314': {
    code: '314',
    channel: IPAWS_PROFILE,
    isError: true,
    label: 'uri-token-invalid',
    remark:
      'The <uri> value is missing http:// or https://, is missing a period, contains a space or ?, or exceeds 2,083 characters.',
  },

  // NWEM/NWR Responses
  '400': {
    code: '400',
    channel: NWEM,
    isError: false,
    label: 'Ack',
    remark: 'A valid NWR/NWEM message was disseminated.',
    disseminated: true,
  },
  '401': {
    code: '401',
    channel: NWEM,
    isError: false,
    label: 'message-not-disseminated-as-NWEM',
    remark: 'The message was not disseminated because of channel blocking or another associated condition.',
    disseminated: false,
  },
  '402': {
    code: '402',
    channel: NWEM,
    isError: true,
    label: 'no-parameter-EAS-ORG',
    remark: 'The alert validated for NWEM dissemination but does not contain the EAS-ORG parameter.',
  },
  '405': {
    code: '405',
    channel: NWEM,
    isError: true,
    label: 'invalid-value-mimeType',
    remark:
      'Broadcast content in <resource><mimeType> does not contain an accepted value: audio/x-ipaws-audio-mp3, audio/x-ipaws-streaming-audio, video/x-ipaws-video, or video/x-ipaws-streaming-video.',
  },
  '406': {
    code: '406',
    channel: NWEM,
    isError: true,
    label: 'invalid-value-in-element-expires',
    remark: 'The <expires> time is more than 99.5 hours after the sent time.',
  },
  '407': {
    code: '407',
    channel: NWEM,
    isError: true,
    label: 'required-element-missing-description',
    remark: 'The <description> element is missing or empty.',
  },
  '412': {
    code: '412',
    channel: NWEM,
    isError: true,
    label: 'system-error-has-occurred-NWEM-service',
    remark: 'An internal NWEM dissemination system error occurred.',
  },
  '415': {
    code: '415',
    channel: NWEM,
    isError: true,
    label: 'signer-not-authorized-for-event-code-NWEM',
    remark: 'The Alert Originator is not authorized for the event code in the COG Profile.',
  },
  '419': {
    code: '419',
    channel: NWEM,
    isError: true,
    label: 'signer-not-authorized-for-geocode-NWEM',
    remark: 'The Alert Originator is not authorized for the geocode in the COG Profile.',
  },
  '421': {
    code: '421',
    channel: NWEM,
    isError: false,
    label: 'exceeded-NWEM-geocode-max-limit',
    remark: 'Advisory indicating that the NWEM CAP alert exceeds the current NWR maximum of 31 geocodes.',
  },

  // EAS Responses
  '500': {
    code: '500',
    channel: EAS,
    isError: false,
    label: 'Ack',
    remark: 'A valid EAS message was disseminated.',
    disseminated: true,
  },
  '501': {
    code: '501',
    channel: EAS,
    isError: false,
    label: 'message-not-disseminated-as-EAS',
    remark: 'The message was not disseminated because of channel blocking or another associated condition.',
    disseminated: false,
  },
  '502': {
    code: '502',
    channel: EAS,
    isError: true,
    label: 'no-parameter-EAS-ORG',
    remark: 'The alert validated for EAS dissemination but does not contain the EAS-ORG parameter.',
  },
  '505': {
    code: '505',
    channel: EAS,
    isError: true,
    label: 'invalid-value-mimeType',
    remark:
      'Broadcast content in <resource><mimeType> does not contain an accepted value: audio/x-ipaws-audio-mp3, audio/x-ipaws-streaming-audio, video/x-ipaws-video, or video/x-ipaws-streaming-video.',
  },
  '506': {
    code: '506',
    channel: EAS,
    isError: true,
    label: 'invalid-value-in-element-expires',
    remark: 'The <expires> time is more than 99.5 hours after the sent time.',
  },
  '507': {
    code: '507',
    channel: EAS,
    isError: true,
    label: 'required-element-missing-description',
    remark: 'The <description> element is missing or empty.',
  },
  '508': {
    code: '508',
    channel: EAS,
    isError: false,
    label: 'invalid-value-character-limit',
    remark:
      'Advisory indicating that the combined character count of <instruction> and <description> exceeds 1,800 characters.',
  },
  '512': {
    code: '512',
    channel: EAS,
    isError: true,
    label: 'system-error-has-occurred-EAS-service',
    remark: 'An internal EAS dissemination system error occurred.',
  },
  '515': {
    code: '515',
    channel: EAS,
    isError: true,
    label: 'signer-not-authorized-for-event-code-EAS',
    remark: 'The Alert Originator is not authorized for the event code in the COG Profile.',
  },
  '519': {
    code: '519',
    channel: EAS,
    isError: true,
    label: 'signer-not-authorized-for-geocode-EAS',
    remark: 'The Alert Originator is not authorized for the geocode in the COG Profile.',
  },
  '521': {
    code: '521',
    channel: EAS,
    isError: false,
    label: 'exceeded-EAS-geocode-max-limit',
    remark: 'Advisory indicating that the EAS CAP alert exceeds the current EAS maximum of 31 geocodes.',
  },

  // CMAS/WEA Responses
  '600': {
    code: '600',
    channel: CMAS,
    isError: false,
    label: 'Ack',
    remark: 'A valid CMAS/WEA message was disseminated.',
    disseminated: true,
  },
  '601': {
    code: '601',
    channel: CMAS,
    isError: false,
    label: 'message-not-disseminated-as-CMAS-Version-2',
    remark: 'The message was not disseminated because of channel blocking or another associated condition.',
    disseminated: false,
  },
  '602': {
    code: '602',
    channel: CMAS,
    isError: true,
    label: 'invalid-value-in-element-responseType',
    remark: '<responseType> contains a value that is invalid for CMAS.',
  },
  '603': {
    code: '603',
    channel: CMAS,
    isError: true,
    label: 'invalid-value-in-element-CMAMtext',
    remark: 'CMAMtext exceeds 90 characters or CMAMlongtext exceeds 360 characters.',
  },
  '604': {
    code: '604',
    channel: CMAS,
    isError: true,
    label: 'lacks-supported-language',
    remark: 'The language value is neither en-US nor empty.',
  },
  '605': {
    code: '605',
    channel: CMAS,
    isError: true,
    label: 'update-not-valid-for-CMAS-Alert-cancelled',
    remark:
      'An Update contains invalid CMAS values for <urgency>, <severity>, or <certainty>, causing a CMAC Cancel message to be disseminated.',
  },
  '608': {
    code: '608',
    channel: CMAS,
    isError: false,
    label: 'marine-zone-SAME-geocode-not-disseminated-CMAS',
    remark: 'Interim status indicating that a Marine Zone geocode was not disseminated to CMAS.',
  },
  '610': {
    code: '610',
    channel: CMAS,
    isError: true,
    label: 'CMAM-Text-is-missing-or-empty',
    remark: 'CMAMtext is missing or empty.',
  },
  '612': {
    code: '612',
    channel: CMAS,
    isError: true,
    label: 'system-error-has-occurred-CMAS-dissemination-service',
    remark: 'An internal CMAS dissemination system error occurred.',
  },
  '613': {
    code: '613',
    channel: CMAS,
    isError: true,
    label: 'invalid-value-in-element-expires',
    remark: 'The expiration time is more than 24 hours after the sent time.',
  },
  '615': {
    code: '615',
    channel: CMAS,
    isError: true,
    label: 'signer-not-authorized-for-event-code-CMAS',
    remark: 'The Alert Originator is not authorized for the event code in the COG Profile.',
  },
  '616': {
    code: '616',
    channel: CMAS,
    isError: false,
    label: 'advisory-unexpected-case-CMAMtext-parameter-name',
    remark:
      'Advisory indicating that the CMAMtext parameter name uses incorrect capitalization and does not exactly match CMAMtext.',
  },
  '619': {
    code: '619',
    channel: CMAS,
    isError: true,
    label: 'signer-not-authorized-for-geocode-CMAS',
    remark: 'The Alert Originator is not authorized for the geocode in the COG Profile.',
  },
  '620': {
    code: '620',
    channel: CMAS,
    isError: false,
    label: 'referenced-alert-never-disseminated-to-CMAS',
    remark: 'Advisory indicating that an Update or Cancel references an alert that was not previously disseminated as a WEA.',
  },
  '621': {
    code: '621',
    channel: CMAS,
    isError: true,
    label: 'invalid-characters-in-element-CMAMtext',
    remark: 'CMAMtext contains one or more prohibited characters: {, }, |, \\, ^, ~, [, or ].',
  },
  '622': {
    code: '622',
    channel: CMAS,
    isError: true,
    label: 'invalid-mismatch-missing-or-empty-wea-handling-code',
    remark:
      'The WEA Handling Code is invalid, missing, empty, misspelled, or incompatible with the event code, such as using the Public Safety handling code with a CAE event code.',
  },
  '623': {
    code: '623',
    channel: CMAS,
    isError: true,
    label: 'exceeds-10-shapes-or-100-nodes',
    remark:
      'The CAP alert\u2019s <area> block contains more than 10 total shapes, including polygons and circles, or more than 100 coordinate pairs across all polygons.',
  },
  '626': {
    code: '626',
    channel: CMAS,
    isError: true,
    label: 'invalid-value-urgency-severity-or-certainty',
    remark: 'The CAP alert does not contain valid <urgency>, <severity>, and <certainty> values.',
  },
  '627': {
    code: '627',
    channel: CMAS,
    isError: false,
    label: 'circle-polygon-coordinate-pairs-exceed-4-decimal-precision',
    remark: 'Advisory indicating that one or more circle or polygon coordinate pairs exceed four decimal places.',
  },

  // Non-EAS Public Feed Responses
  '800': {
    code: '800',
    channel: PUBLIC_FEED,
    isError: false,
    label: 'Ack (Public Feeds)',
    remark: 'A valid non-EAS public message was disseminated.',
    disseminated: true,
  },
  '801': {
    code: '801',
    channel: PUBLIC_FEED,
    isError: false,
    label: 'message-not-disseminated-as-non-EAS-public',
    remark: 'The message was not disseminated because it is an EAS message or because of another associated condition.',
    disseminated: false,
  },
  '812': {
    code: '812',
    channel: PUBLIC_FEED,
    isError: true,
    label: 'system-error-has-occurred-public-dissemination-service',
    remark: 'An internal public dissemination system error occurred.',
  },
  '815': {
    code: '815',
    channel: PUBLIC_FEED,
    isError: true,
    label: 'signer-not-authorized-for-event-code-PUBLIC',
    remark: 'The Alert Originator is not authorized for the event code in the COG Profile.',
  },
  '819': {
    code: '819',
    channel: PUBLIC_FEED,
    isError: true,
    label: 'signer-not-authorized-for-geocode-PUBLIC',
    remark: 'The Alert Originator is not authorized for the geocode in the COG Profile.',
  },
};

const UNKNOWN_STATUS_CHANNEL: FemaStatusChannel = 'UNKNOWN';

/** FEMA's TDL Message Viewer — the operator's proof an alert reached IPAWS (BE-09). */
export const FEMA_MESSAGE_VIEWER_URL = 'https://messageviewer.tdl.apps.fema.gov/index.html';


/** Looks up a FEMA status/advisory code; codes outside the registry return a safe unknown entry. */
export const getFemaStatusInfo = (code: string): FemaStatusCodeInfo =>
  FEMA_STATUS_CODES[code] ?? {
    code,
    channel: UNKNOWN_STATUS_CHANNEL,
    isError: false,
    label: 'unrecognized-status-code',
    remark: 'This status code is not in the RescAlert registry. Treat it as informational only.',
  };

/** Operator-facing channel names for the result modal's advisory list. */
export const FEMA_STATUS_CHANNEL_LABELS: Record<FemaStatusChannel, string> = {
  MOBILE_CARRIER: 'Mobile Carrier Gateway',
  CAP_EXCHANGE: 'CAP Exchange',
  IPAWS_PROFILE: 'IPAWS Profile',
  NWEM: 'NWEM/NWR',
  EAS: 'EAS',
  CMAS: 'CMAS/WEA',
  PUBLIC_FEED: 'Non-EAS Public Feed',
  UNKNOWN: 'Unrecognized',
};
